import { motion } from 'framer-motion';

// Check for reduced motion preference
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Animation durations and delays
export const ANIMATION_DURATION = {
  FAST: 0.15,
  NORMAL: 0.3,
  SLOW: 0.5,
  CARD_ENTRANCE: 0.4,
  NUMBER_COUNTING: 1,
  SKELETON_PULSE: 1.5,
};

export const ANIMATION_DELAY = {
  CARD_STAGGER: 0.1,
  ROW_STAGGER: 0.05,
  LIST_ITEM_STAGGER: 0.05,
};

export const EASING = {
  EASE_IN_OUT: 'easeInOut',
  EASE_OUT: 'easeOut',
  EASE_IN: 'easeIn',
};

// Page transition variants
export const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export const pageTransition = {
  type: 'tween',
  ease: EASING.EASE_IN_OUT,
  duration: prefersReducedMotion() ? 0 : ANIMATION_DURATION.NORMAL,
};

// Card animation variants
export const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATION.CARD_ENTRANCE,
      delay: prefersReducedMotion() ? 0 : index * ANIMATION_DELAY.CARD_STAGGER,
      ease: EASING.EASE_OUT,
    },
  }),
};

export const cardHoverVariants = {
  rest: { y: 0, boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' },
  hover: {
    y: -4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: {
      duration: ANIMATION_DURATION.FAST,
      ease: EASING.EASE_OUT,
    },
  },
};

// List item variants for stagger animations
export const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (index = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATION.NORMAL,
      delay: prefersReducedMotion() ? 0 : index * ANIMATION_DELAY.LIST_ITEM_STAGGER,
      ease: EASING.EASE_OUT,
    },
  }),
};

// Container variants for stagger children
export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: prefersReducedMotion() ? 0 : ANIMATION_DELAY.ROW_STAGGER,
    },
  },
};

// Table row variants
export const tableRowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (index = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATION.NORMAL,
      delay: prefersReducedMotion() ? 0 : index * ANIMATION_DELAY.ROW_STAGGER,
      ease: EASING.EASE_OUT,
    },
  }),
};

// Modal/Drawer variants
export const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: prefersReducedMotion() ? 0 : 0.2,
      ease: EASING.EASE_OUT,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: prefersReducedMotion() ? 0 : 0.2,
      ease: EASING.EASE_IN,
    },
  },
};

// Button variants
export const buttonVariants = {
  rest: { scale: 1 },
  hover: {
    scale: 1.02,
    transition: {
      duration: ANIMATION_DURATION.FAST,
      ease: EASING.EASE_OUT,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: ANIMATION_DURATION.FAST,
      ease: EASING.EASE_OUT,
    },
  },
};

// Skeleton pulse variants
export const skeletonVariants = {
  pulse: {
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: ANIMATION_DURATION.SKELETON_PULSE,
      repeat: Infinity,
      ease: EASING.EASE_IN_OUT,
    },
  },
};

// Fade in variants
export const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: ANIMATION_DURATION.NORMAL,
      ease: EASING.EASE_OUT,
    },
  },
};

// Slide in variants
export const slideInVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATION.NORMAL,
      ease: EASING.EASE_OUT,
    },
  },
};

// Scale variants
export const scaleVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATION.NORMAL,
      ease: EASING.EASE_OUT,
    },
  },
};

// Animated wrapper components
export const AnimatedCard = ({ children, index = 0, className = '', ...props }) => {
  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover={prefersReducedMotion() ? {} : cardHoverVariants.hover}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedButton = ({ children, className = '', ...props }) => {
  return (
    <motion.div
      variants={buttonVariants}
      initial="rest"
      whileHover={prefersReducedMotion() ? 'rest' : 'hover'}
      whileTap={prefersReducedMotion() ? 'rest' : 'tap'}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedListItem = ({ children, index = 0, className = '', ...props }) => {
  return (
    <motion.div
      variants={listItemVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};


